/* eslint-disable react-hooks/exhaustive-deps */
import { Search, RotateCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import Loader from '../../../components/atoms/Loader'
import { BadgeStatusPrLists } from '../../../components/atoms/StatusLabel'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import RowEmpty from '../../../components/atoms/RowEmpty'
import api from '../../../config/AxiosInstance'
import toast, { Toaster } from 'react-hot-toast'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const STATUSES = [
  { value: 'waiting_pnp', label: 'Waiting PNP' },
  { value: 'pnp_picking', label: 'PNP Picking' },
  { value: 'waiting_rnr', label: 'Waiting RNR' },
  { value: 'repacking', label: 'Repacking' },
  { value: 'unsubmitted', label: 'Unsubmitted' },
  { value: 'placing', label: 'Placing' },
  { value: 'done', label: 'Done' }
]

function WorkorderJobStatus() {
  const navigate = useNavigate()
  const { state } = useLocation()

  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState(state?.q || '')
  const [debouncedQ, setDebouncedQ] = useState(q)
  const [status, setStatus] = useState(state?.status || '')
  const [selectedWarehouse, setSelectedWarehouse] = useState(
    state?.warehouse_id || ''
  )
  const [warehouses, setWarehouses] = useState([])

  const [data, setData] = useState({
    values: [],
    metadata: {
      per_page: 10,
      current_page: 1,
      total_row: 0,
      total_page: 0
    }
  })

  const refSrcWarehouses = useRef()
  const refSrcStatus = useRef()

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQ(q)
      setData((prev) => ({
        ...prev,
        metadata: { ...prev.metadata, current_page: 1 }
      }))
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [q])

  const getWarehouses = async () => {
    try {
      const params = {
        type: 'internal',
        forUser: true,
        child: true
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      const options = data.map((w) => ({
        value: w.id,
        label: w.warehouse_name
      }))
      setWarehouses(options)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  useEffect(() => {
    getWarehouses()
  }, [])

  const fetchData = async (page = null) => {
    try {
      setLoading(true)
      const params = {
        per_page: data?.metadata?.per_page || 10,
        page: page || data?.metadata?.current_page || 1,
        q: debouncedQ,
        status,
        warehouse_id: selectedWarehouse
      }
      const res = await api.get('job-status', { params })
      const { data: dataResponse } = res
      if (dataResponse.success) {
        const { data: dataValues, metadata: metaResponse } = dataResponse
        setData({
          values: dataValues,
          metadata: metaResponse
        })
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [
    data?.metadata?.per_page,
    data?.metadata?.current_page,
    debouncedQ,
    status,
    selectedWarehouse
  ])

  const handlePageChange = (data) => {
    setData((prevData) => ({
      ...prevData,
      metadata: {
        ...prevData.metadata,
        current_page: data.selected + 1
      }
    }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setData((prevData) => ({
      ...prevData,
      metadata: {
        ...prevData.metadata,
        current_page: 1,
        per_page: parseInt(e.target.value, 10)
      }
    }))
    window.scrollTo(0, 0)
  }

  const handleReset = () => {
    setQ('')
    setDebouncedQ('')
    setStatus('')
    setSelectedWarehouse('')
    refSrcWarehouses.current?.clearValue()
    refSrcStatus.current?.clearValue()
    setData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, current_page: 1 }
    }))
  }

  const renderRelatedTicket = (value) => {
    if (value.workorder_code) {
      return (
        <div key={value.id}>
          <Link
            to={`/workorder/repack/detail/${value.workorder_code}`}
            className="text-decoration-none fw-bold"
          >
            {value.workorder_code}
          </Link>
        </div>
      )
    }
    return '-'
  }

  const renderAction = (dataValue) => (
    <div key={dataValue.id} className="d-flex justify-content-end">
      <button
        type="button"
        className="btn btn-light text-secondary border rounded"
        style={{ width: '32px', height: '32px', padding: 0 }}
        onClick={() =>
          navigate(`/workorder/job-status/detail/${dataValue.id}`, {
            state: {
              q,
              status,
              warehouse_id: selectedWarehouse
            }
          })
        }
        title="View Detail"
      >
        <Search size={16} />
      </button>
    </div>
  )

  const downloadBeritaAcara = (dataValue) => (
    <div key={dataValue.id} className="d-flex justify-content-end">
      <button
        type="button"
        className={`btn btn-sm rounded px-3 ${
          dataValue.status !== 'done'
            ? 'btn-secondary opacity-25'
            : 'btn-primary-blue text-light'
        }`}
        disabled={dataValue.status !== 'done'}
        onClick={() => {}}
      >
        Download
      </button>
    </div>
  )

  const columns = [
    { key: 'job_code', label: '#Ticket' },
    {
      key: 'workorder_code',
      label: '#Work Order',
      component: (value) => renderRelatedTicket(value)
    },
    { key: 'warehouse_name', label: 'Warehouse', className: 'text-end' },
    {
      key: 'destination_area',
      label: 'Destination Area',
      className: 'text-end',
      component: (value) => value?.destination_area || '-'
    },
    {
      key: 'status',
      label: 'Status',
      className: 'text-end',
      component: (value) => (
        <div key={value.id} className="d-flex justify-content-end">
          <BadgeStatusPrLists text={value?.status.replaceAll('_', ' ')} />
        </div>
      )
    },
    {
      key: 'berita_acara',
      label: 'Berita Acara',
      className: 'text-end',
      component: (value) => downloadBeritaAcara(value)
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end',
      component: (value) => renderAction(value)
    }
  ]

  return (
    <>
      <Toaster />
      <Container fluid className="px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Job Status</h3>
          </Col>
          <Col className="text-end">
            <Button
              variant="light"
              className="text-secondary fw-bold rounded px-3 border"
              onClick={handleReset}
              style={{ fontSize: '0.85rem' }}
            >
              <RotateCw size={16} className="me-2" />
              Reset Filters
            </Button>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>#Ticket</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRight: 'none',
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px'
                    }}
                  >
                    <Search size={14} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search Ticket..."
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                    onChange={(e) => {
                      setQ(e.target.value)
                    }}
                    value={q}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Warehouse</Form.Label>
                <Select
                  isClearable
                  ref={refSrcWarehouses}
                  options={warehouses}
                  styles={SelectStyles}
                  placeholder="All Warehouses"
                  onChange={(e) => {
                    setSelectedWarehouse(e?.value || '')
                    setData((prev) => ({
                      ...prev,
                      metadata: { ...prev.metadata, current_page: 1 }
                    }))
                  }}
                  value={
                    selectedWarehouse
                      ? warehouses.find((w) => w.value === selectedWarehouse)
                      : null
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Status</Form.Label>
                <Select
                  isClearable
                  ref={refSrcStatus}
                  options={STATUSES}
                  styles={SelectStyles}
                  placeholder="All Statuses"
                  onChange={(e) => {
                    setStatus(e?.value || '')
                    setData((prev) => ({
                      ...prev,
                      metadata: { ...prev.metadata, current_page: 1 }
                    }))
                  }}
                  value={
                    status ? STATUSES.find((s) => s.value === status) : null
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <Row>
          <Col md={12}>
            <Table
              hover
              responsive
              className="shadow-sm mb-5 bg-white"
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                fontSize: '0.9rem'
              }}
            >
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      className={`p-2 align-middle ${column.className}`}
                      key={column.key}
                      style={tableHeaderStyle}
                    >
                      <span>{column.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={columns.length} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && data?.values?.length === 0 && (
                  <RowEmpty colSpan={columns.length} />
                )}
                {!loading &&
                  data?.values?.length > 0 &&
                  data?.values?.map((item, index) => (
                    <tr key={`${item.id}-${index}`}>
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`align-middle p-3 ${column.className}`}
                        >
                          {column.component && column.component(item)}
                          {!column.component && item[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="p-3 border-top-0" colSpan={columns.length}>
                    <TablePagination
                      meta={data.metadata}
                      handlePageChange={handlePageChange}
                      handleOnChangeMeta={handleOnChangeMeta}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default WorkorderJobStatus
